import CircularLoaderVue from "@/components/CircularLoader.vue";
import SkeletonLoader from "@/components/SkeletonLoader.vue";
import { alertPlugin } from "@/plugins/alert";
import type { MSite } from "@/types/core";
import { format } from "date-fns";
import { createPinia, setActivePinia } from "pinia";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";
import { createApp } from "vue";
// base styles
import { MUser } from "@/types/account";
import "@konigle/pixel/styles/pixel.css";
import "../css/main.css";
import { selectedShopKey, sitesKey, userKey } from "./keys";

/**
 * Returns the selected shop from local storage or the first shop in the list
 * if no shop is selected.
 * @returns {Object} The selected shop object.
 */
export function getShopsInfo(): { shops: MSite[]; selectedShop: MSite | null } {
  const shops = JSON.parse(
    document.getElementById("shops-data")?.textContent || "[]",
  );

  let selectedShopId = localStorage.getItem("selectedShop");
  let selectedShop = null;

  if (selectedShopId && shops && shops?.length) {
    selectedShop = shops.find((s: any) => s.uid === selectedShopId);
    // shop might have deleted for some reason - during development
    if (!selectedShop) {
      selectedShop = shops[0];
      localStorage.setItem("selectedShop", selectedShop.uid);
    }
  } else {
    if (shops.length) {
      selectedShop = shops[0];
      localStorage.setItem("selectedShop", selectedShop.uid);
    }
  }

  return {
    shops,
    selectedShop,
  };
}

function getUser(): MUser | null {
  const userData = document.getElementById("logged-in-user")?.textContent;
  if (!userData) {
    return null;
  }
  try {
    return JSON.parse(userData);
  } catch (e) {
    console.error("Failed to parse user data:", e);
    return null;
  }
}

function getRootProps() {
  // get all json scripts whose id is like "vue-app-data:*"
  const rootProps: { [key: string]: any } = {};
  const jsonScripts = document.querySelectorAll(
    "script[id^='vue-app-data:'][type='application/json']",
  );
  jsonScripts.forEach((script) => {
    // the second part of the id coverted to camelCase is the prop name
    const id = script.id.replace("vue-app-data:", "");
    // covert id to camelCase
    const key = id.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
    const data = JSON.parse(script.textContent || "{}");

    // if the key is empty and the data is an object, spread the object
    // into the root props
    if (!key && typeof data === "object") {
      Object.assign(rootProps, data);
    } else {
      rootProps[key] = data;
    }
  });

  return rootProps;
}

/**
 * Injects the Vue.js app into the DOM.
 * @param {Object} component - The root Vue component.
 * @returns {Object} The Vue app instance.
 */
export function injectApp(component: any, mount: boolean = true) {
  const appContainer = document.getElementById("__vue_app");
  if (!appContainer) {
    console.error(
      "No app container found. Please add a div with id '__vue_app' to your template.",
    );
    return;
  }
  const rootProps = getRootProps();

  const app = createApp(component, rootProps);
  const pinia = createPinia();
  pinia.use(piniaPluginPersistedstate);

  // provide selected shop to all components
  const { selectedShop, shops } = getShopsInfo();
  app.provide(selectedShopKey, selectedShop);
  app.provide(sitesKey, shops);

  const user = getUser();
  app.provide(userKey, user);

  // register global components
  app.component("SkeletonLoader", SkeletonLoader);
  app.component("CircularLoader", CircularLoaderVue);
  app.config.globalProperties.formatTimestamp = (
    timestamp: string,
    placeholder: string = "-",
  ) => {
    if (!timestamp) {
      return placeholder;
    }
    return format(new Date(timestamp), "MMM dd yyyy, hh:mm a");
  };
  app.config.globalProperties.formatDate = (date: string) => {
    return format(new Date(date), "MMM dd yyyy");
  };

  // register global plugins
  app.use(alertPlugin);

  app.use(pinia);
  // This is required to use pinia in libraries like sitekit. Tried everything
  // but this is the only way to make it work. The library will have to use
  // window.__pinia to when using store.
  setActivePinia(pinia);
  (window as any).__pinia = pinia;

  // assume that the HTML templates will have this ID for the desired
  // mount point
  if (mount) {
    app.mount("#__vue_app");
  }
  return app;
}
